// src/pages/DocumentManagementPage.jsx
// ============================================================
// DOCUMENT MANAGEMENT PAGE - EA CONSISTENCY + VALUE SIGNALING
// 
// Refactored per ChatGPT spec:
// - "Evidence Injection" UX framing
// - Value-oriented document cards (not file table)
// - DVA design system (Navy + Orange)
// - Strategic copy throughout
// - Collapsible "How it works"
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE, apiFetch, IS_STATIC_DEMO } from '../config/apiBase';
import useDemoUploads from '../prototype/hooks/useDemoUploads';
import { markPersonalizationChanged } from '../prototype/hooks/useEvaluationRun';
import { evidenceForFile, uploadOutcome } from '../lib/coverage/buildCoverageInputs';
import { EAUploadPathsPanel } from '../prototype/components/EAUploadPathsPanel';
import { 
  Upload,
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  RefreshCw, 
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Info,
  Sparkles,
  Eye,
  Trash2,
  Loader,
  X,
  File
} from 'lucide-react';

// ============================================================
// DVA DESIGN SYSTEM COLORS
// ============================================================
const DVA_COLORS = {
  navy: '#18365E',
  orange: '#FF6E4C',
  orangeLight: '#FFF4F1',
  slate: '#717182',
  slateLight: '#F5F7FA',
  border: '#E5E7EB',
  success: '#10B981',
  successLight: '#ECFDF5',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  error: '#EF4444',
  errorLight: '#FEF2F2',
};

// ============================================================
// EA FOCUS MODE DETECTION HELPER
// ============================================================
function useEAFocusMode() {
  const location = useLocation();
  const locationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  
  const isEA = locationState.fromEA === true || searchParams.get('fromEA') === 'true';
  const returnTo = locationState.returnTo || '/home';
  const restoreScrollY = locationState.restoreScrollY || 0;
  
  return { isEA, returnTo, restoreScrollY };
}

// ============================================================
// EA FOCUS BANNER COMPONENT (Minimized breadcrumb per spec)
// ============================================================
function EAFocusBanner({ returnTo, restoreScrollY }) {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(returnTo, {
      state: { restoreScrollY }
    });
  };

  return (
    <div style={{ backgroundColor: DVA_COLORS.navy }} className="text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Personalize
          </button>

          {/* Minimal breadcrumb - just context hint */}
          <span className="text-xs text-white/50">
            EA Personalization
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DOCUMENT CARD COMPONENT (Value-Oriented with Signals Badge)
// ============================================================
function DocumentCard({ doc, onViewDetails, onRemove }) {
  const [isHovered, setIsHovered] = useState(false);

  const statusConfig = {
    'uploaded': { 
      icon: Clock, 
      label: 'Uploaded', 
      bgColor: DVA_COLORS.warningLight, 
      textColor: DVA_COLORS.warning 
    },
    'scanned': { 
      icon: CheckCircle, 
      label: 'Scanned', 
      bgColor: DVA_COLORS.slateLight, 
      textColor: DVA_COLORS.slate 
    },
    'processing': { 
      icon: Loader, 
      label: 'Processing...', 
      bgColor: DVA_COLORS.orangeLight, 
      textColor: DVA_COLORS.orange 
    },
    'processed': { 
      icon: CheckCircle, 
      label: 'Ready', 
      bgColor: DVA_COLORS.successLight, 
      textColor: DVA_COLORS.success 
    },
    'failed': { 
      icon: XCircle, 
      label: 'Failed', 
      bgColor: DVA_COLORS.errorLight, 
      textColor: DVA_COLORS.error 
    },
    'quarantined': { 
      icon: AlertCircle, 
      label: 'Quarantined', 
      bgColor: DVA_COLORS.errorLight, 
      textColor: DVA_COLORS.error 
    }
  };

  const config = statusConfig[doc.status] || statusConfig['uploaded'];
  const StatusIcon = config.icon;
  const isProcessing = doc.status === 'processing';
  const isComplete = doc.status === 'processed';

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp.value || timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileTypeLabel = (fileType) => {
    const types = { 'txt': 'TXT', 'csv': 'CSV', 'pdf': 'PDF', 'md': 'MD' };
    return types[fileType] || fileType?.toUpperCase() || 'DOC';
  };

  // Calculate signals found (from stats or mock)
  const signalsFound = doc.stats?.fact_count || (isComplete ? Math.floor(Math.random() * 8) + 3 : 0);

  return (
    <div
      className="bg-white rounded-2xl border-2 transition-all duration-200"
      style={{
        borderColor: isHovered ? DVA_COLORS.orange : DVA_COLORS.border,
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* File Type Badge */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: DVA_COLORS.slateLight }}
          >
            <span className="text-xs font-bold" style={{ color: DVA_COLORS.navy }}>
              {getFileTypeLabel(doc.file_type)}
            </span>
          </div>

          {/* Document Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4
                  className="text-sm font-semibold truncate"
                  style={{ color: DVA_COLORS.navy }}
                >
                  {doc.title || doc.filename}
                </h4>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                    {formatDate(doc.created_at)}
                  </span>
                  {doc.bytes_total && (
                    <>
                      <span style={{ color: DVA_COLORS.border }}>•</span>
                      <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                        {formatFileSize(doc.bytes_total)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Right side: Status + Signals */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Signals Found Badge */}
                {isComplete && signalsFound > 0 && (
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded-lg"
                    style={{ backgroundColor: DVA_COLORS.orangeLight }}
                  >
                    <Sparkles className="w-3 h-3" style={{ color: DVA_COLORS.orange }} />
                    <span className="text-xs font-medium" style={{ color: DVA_COLORS.orange }}>
                      {signalsFound} signals
                    </span>
                  </div>
                )}

                {/* Status Badge */}
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                  style={{
                    backgroundColor: config.bgColor,
                    color: config.textColor,
                  }}
                >
                  <StatusIcon
                    className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`}
                  />
                  <span className="text-xs font-medium">{config.label}</span>
                </div>
              </div>
            </div>

            {/* Contribution tags for completed docs */}
            {isComplete && (
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                  Enhances:
                </span>
                {['Strategic Alignment', 'ROI Assumptions'].map((tag, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-medium px-2 py-0.5 rounded-md"
                    style={{
                      backgroundColor: DVA_COLORS.slateLight,
                      color: DVA_COLORS.navy,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions Row */}
      <div
        className="flex items-center justify-end gap-2 px-4 py-3 border-t"
        style={{ borderColor: DVA_COLORS.border }}
      >
        <button
          onClick={() => onViewDetails?.(doc)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            color: DVA_COLORS.navy,
            backgroundColor: DVA_COLORS.slateLight,
          }}
        >
          <Eye className="w-3.5 h-3.5" />
          View details
        </button>
        <button
          onClick={() => onRemove?.(doc)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-red-50"
          style={{ color: DVA_COLORS.slate }}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Remove
        </button>
      </div>
    </div>
  );
}

// ============================================================
// DOCUMENT DETAILS MODAL (Right-side drawer style)
// ============================================================
function DocumentDetailsModal({ doc, isOpen, onClose }) {
  if (!isOpen || !doc) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.value || timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Mock extracted signals
  const extractedSignals = [
    { type: 'Priority', value: 'Digital transformation investment', confidence: 'High' },
    { type: 'Constraint', value: 'Budget ceiling of $2.5M', confidence: 'Medium' },
    { type: 'Board Metric', value: 'Patient satisfaction > 85%', confidence: 'High' },
    { type: 'Timeline', value: 'FY2026 implementation target', confidence: 'Medium' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div 
        className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto"
        style={{ animation: 'slideIn 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between" style={{ borderColor: DVA_COLORS.border }}>
          <h2 className="text-lg font-semibold" style={{ color: DVA_COLORS.navy }}>
            Document Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: DVA_COLORS.slate }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: DVA_COLORS.navy }}>
              {doc.title || doc.filename}
            </h3>
            <div className="space-y-2 text-sm" style={{ color: DVA_COLORS.slate }}>
              <div className="flex justify-between">
                <span>File Type</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {doc.file_type?.toUpperCase() || 'DOC'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Size</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {formatFileSize(doc.bytes_total)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Uploaded</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {formatDate(doc.created_at)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Status</span>
                <span 
                  className="font-medium"
                  style={{ color: doc.status === 'processed' ? DVA_COLORS.success : DVA_COLORS.warning }}
                >
                  {doc.status === 'processed' ? 'Ready' : doc.status}
                </span>
              </div>
            </div>
          </div>

          {/* Extracted Signals */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: DVA_COLORS.orange }} />
              <h3 className="text-sm font-semibold" style={{ color: DVA_COLORS.navy }}>
                Extracted Signals
              </h3>
            </div>
            
            <div className="space-y-3">
              {extractedSignals.map((signal, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: DVA_COLORS.slateLight }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold" style={{ color: DVA_COLORS.orange }}>
                      {signal.type}
                    </span>
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ 
                        backgroundColor: signal.confidence === 'High' ? DVA_COLORS.successLight : DVA_COLORS.warningLight,
                        color: signal.confidence === 'High' ? DVA_COLORS.success : DVA_COLORS.warning,
                      }}
                    >
                      {signal.confidence}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: DVA_COLORS.navy }}>
                    {signal.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* How this helps */}
          <div 
            className="p-4 rounded-xl"
            style={{ backgroundColor: DVA_COLORS.orangeLight }}
          >
            <p className="text-xs" style={{ color: DVA_COLORS.slate }}>
              These signals are used to enhance opportunity rankings, refine ROI assumptions, and strengthen executive defensibility.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// REMOVE CONFIRMATION MODAL
// ============================================================
function RemoveConfirmModal({ doc, isOpen, onClose, onConfirm }) {
  if (!isOpen || !doc) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="text-center">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: DVA_COLORS.errorLight }}
          >
            <Trash2 className="w-6 h-6" style={{ color: DVA_COLORS.error }} />
          </div>
          
          <h3 className="text-lg font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
            Remove Document?
          </h3>
          <p className="text-sm mb-6" style={{ color: DVA_COLORS.slate }}>
            This will remove "<span className="font-medium">{doc.title || doc.filename}</span>" and its extracted signals from your analysis.
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors"
              style={{ 
                borderColor: DVA_COLORS.border, 
                color: DVA_COLORS.slate 
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm(doc.document_id);
                onClose();
              }}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: DVA_COLORS.error }}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PROGRESS INDICATOR COMPONENT
// ============================================================
function DocumentsProgressIndicator({ documentCount, pendingCount = 0 }) {
  const getProgress = (count) => {
    if (count === 0) return 0;
    if (count === 1) return 33;
    if (count === 2) return 66;
    return 100;
  };

  const totalCount = documentCount + pendingCount;
  const progress = getProgress(totalCount);
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: DVA_COLORS.border }}>
        <div 
          className="h-1.5 rounded-full transition-all duration-500"
          style={{ 
            width: `${progress}%`,
            backgroundColor: progress === 100 ? DVA_COLORS.success : DVA_COLORS.orange 
          }}
        />
      </div>
      <span className="text-xs font-medium" style={{ color: DVA_COLORS.slate }}>
        {progress}%
      </span>
    </div>
  );
}
// ============================================================
// UPLOAD FILE CARD (for upload queue)
// ============================================================
function UploadFileCard({ file, onRemove, onRetry }) {
  const statusConfig = {
    'ready': { icon: Clock, label: 'Queued', color: DVA_COLORS.slate, bgColor: DVA_COLORS.slateLight },
    'uploading': { icon: Loader, label: 'Uploading...', color: DVA_COLORS.orange, bgColor: DVA_COLORS.orangeLight },
    'processing': { icon: Loader, label: 'Processing...', color: DVA_COLORS.orange, bgColor: DVA_COLORS.orangeLight },
    'complete': { icon: CheckCircle, label: 'Complete', color: DVA_COLORS.success, bgColor: DVA_COLORS.successLight },
    'error': { icon: XCircle, label: 'Processing failed', color: DVA_COLORS.error, bgColor: DVA_COLORS.errorLight },
  };

  const config = statusConfig[file.status] || statusConfig['ready'];
  const StatusIcon = config.icon;
  const isProcessing = file.status === 'uploading' || file.status === 'processing';
  const isError = file.status === 'error';
  const isComplete = file.status === 'complete';

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div
      className="bg-white rounded-xl border-2 p-4 transition-all"
      style={{
        borderColor: isError ? DVA_COLORS.error : 
                     isComplete ? DVA_COLORS.success : 
                     isProcessing ? DVA_COLORS.orange : DVA_COLORS.border,
        backgroundColor: isProcessing ? DVA_COLORS.orangeLight : 'white',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: config.bgColor }}
        >
          <FileText className="w-5 h-5" style={{ color: config.color }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" style={{ color: DVA_COLORS.navy }}>
                {file.name}
              </p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                  {formatFileSize(file.size)}
                </span>
                <div className="flex items-center gap-1.5" style={{ color: config.color }}>
                  <StatusIcon className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`} />
                  <span className="text-xs font-medium">{config.label}</span>
                </div>
              </div>
              {/* What the file did to coverage. Present whether or not it applied: a
                  workbook that could not be read must say so rather than look successful. */}
              {file.coverageNote && (
                <p
                  className="text-xs mt-1.5 leading-snug"
                  style={{ color: file.coverageApplied ? DVA_COLORS.navy : DVA_COLORS.slate }}
                >
                  {file.coverageNote}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Retry button for failed uploads */}
              {isError && onRetry && (
                <button
                  onClick={() => onRetry(file.id)}
                  className="px-2 py-1 rounded-lg text-xs font-medium transition-colors"
                  style={{ 
                    backgroundColor: DVA_COLORS.orangeLight,
                    color: DVA_COLORS.orange 
                  }}
                >
                  Retry
                </button>
              )}
              
              <button
                onClick={() => onRemove(file.id)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                disabled={isProcessing}
                style={{ opacity: isProcessing ? 0.5 : 1 }}
              >
                <X className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {isProcessing && (
            <div className="mt-3">
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${file.progress}%`,
                    backgroundColor: DVA_COLORS.orange,
                  }}
                />
              </div>
              <p className="text-xs mt-1" style={{ color: DVA_COLORS.orange }}>
                {file.status === 'uploading' ? 'Uploading to server...' : 'Extracting insights...'}
              </p>
            </div>
          )}

          {/* Success message */}
          {isComplete && (
            <div className="mt-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: DVA_COLORS.success }} />
              <span className="text-xs" style={{ color: DVA_COLORS.success }}>
                Document processed successfully
              </span>
            </div>
          )}

          {/* Error message with details */}
          {isError && file.error && (
            <div className="mt-2 p-2 rounded-lg" style={{ backgroundColor: DVA_COLORS.errorLight }}>
              <p className="text-xs" style={{ color: DVA_COLORS.error }}>
                {file.error}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const DocumentManagementPage = ({ clientId: propClientId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // EA Focus Mode
  const { isEA, returnTo, restoreScrollY } = useEAFocusMode();

  // Auth & Client
  const { session, sessionLoading } = useAuth();
  const sessionClientId = session?.client_id || null;
  const clientId = propClientId || sessionClientId;

  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upload');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Demo upload store + completion tracker. Docs completion changes ONLY here (on a real
  // upload or delete) — never on opening or closing this page.
  const { documents: demoDocuments, addDocument, removeDocument } = useDemoUploads();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [detailsDoc, setDetailsDoc] = useState(null);
  const [removeDoc, setRemoveDoc] = useState(null);
  const [isDropzoneProcessing, setIsDropzoneProcessing] = useState(false);

  const API_BASE_URL = API_BASE;

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (sessionLoading && !propClientId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: DVA_COLORS.slateLight }}>
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: DVA_COLORS.navy }} />
          <p style={{ color: DVA_COLORS.slate }}>Loading your session...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // NO CLIENT ID STATE
  // ============================================================
  if (!clientId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: DVA_COLORS.slateLight }}>
        <div className="text-center max-w-md px-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: DVA_COLORS.orangeLight }}
          >
            <AlertCircle className="w-8 h-8" style={{ color: DVA_COLORS.orange }} />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
            No Organization Selected
          </h2>
          <p className="mb-6" style={{ color: DVA_COLORS.slate }}>
            Your account is not currently associated with an organization.
            Please contact your administrator or complete onboarding.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-2.5 rounded-xl text-white font-medium transition-colors"
            style={{ backgroundColor: DVA_COLORS.navy }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // FETCH DOCUMENTS
  // ============================================================
  useEffect(() => {
    if (!clientId) return;
    // Fetch on initial load to get accurate count for progress indicator
    fetchDocuments();
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    if (activeTab === 'library') {
      fetchDocuments();
    }
  }, [activeTab]);

  // Static/no-backend demo seed so the grid isn't empty on Cloudflare (Document Upload = 100%).
  const DEMO_DOCUMENTS = [
    { document_id: 'demo-doc-1', title: 'FY26 Strategic Plan', filename: 'fy26-strategic-plan.pdf', file_type: 'pdf', status: 'processed', uploaded_at: '2026-01-15T00:00:00Z', size: 2_400_000 },
    { document_id: 'demo-doc-2', title: 'Annual Report 2025', filename: 'annual-report-2025.pdf', file_type: 'pdf', status: 'processed', uploaded_at: '2026-01-16T00:00:00Z', size: 5_100_000 },
    { document_id: 'demo-doc-3', title: 'Board Priorities Brief', filename: 'board-priorities.docx', file_type: 'docx', status: 'processed', uploaded_at: '2026-01-18T00:00:00Z', size: 640_000 },
  ];

  // The library shows the seeded documents PLUS everything the presenter has uploaded this
  // session, by real filename. Derived (not state) so it can never drift from the store.
  const demoDocsAsLibraryRows = demoDocuments.map(d => ({
    document_id: d.id,
    title: d.name.replace(/\.[^.]+$/, ''),
    filename: d.name,
    file_type: (d.name.split('.').pop() || '').toLowerCase(),
    status: 'processed',
    uploaded_at: d.uploadedAt,
    size: d.size,
  }));

  // Seeded library rows first, then this session's uploads.
  const allLibraryDocs = [...documents, ...demoDocsAsLibraryRows];

  const fetchDocuments = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const response = await apiFetch(`${API_BASE_URL}/api/v2/documents?clientId=${clientId}`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.data || data.documents || []);
      } else {
        setDocuments(DEMO_DOCUMENTS); // backend reachable but no data → demo seed
      }
    } catch (error) {
      // No backend (static demo) → seed demo documents instead of an empty grid.
      console.warn('Documents fetch unavailable; using demo seed:', error);
      setDocuments(DEMO_DOCUMENTS);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FILE HANDLING
  // ============================================================
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    // Any file type. The beat is "drop the documents you already have", so refusing an
    // extension on camera is a worse failure than accepting something we only list by name.
    const allowedTypes = null;
    const maxSize = 25 * 1024 * 1024;

    const newFiles = Array.from(fileList).map(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      let status = 'ready';
      let error = null;

      if (allowedTypes && !allowedTypes.includes(extension)) {
        status = 'error';
        error = 'File type not supported.';
      } else if (file.size > maxSize) {
        status = 'error';
        error = 'File size exceeds 25MB limit.';
      }

      return {
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        size: file.size,
        type: extension,
        status,
        error,
        progress: 0,
        documentId: null
      };
    });

    setFiles(prev => [...prev, ...newFiles]);
    
    // Auto-upload valid files after a brief delay to show them in the queue
    const validFiles = newFiles.filter(f => f.status === 'ready');
    if (validFiles.length > 0) {
      setIsDropzoneProcessing(true);
      setTimeout(() => {
        uploadFilesSequentially(validFiles);
      }, 500);
    }
  };

  // Upload files one by one
  const uploadFilesSequentially = async (filesToUpload) => {
    setUploading(true);
    
    for (const fileItem of filesToUpload) {
      await uploadFile(fileItem);
    }
    
    setUploading(false);
    setIsDropzoneProcessing(false);
    
    // Refresh document list after uploads complete
    fetchDocuments();
  };

  // Retry a failed upload
  const retryFile = async (fileId) => {
    const fileToRetry = files.find(f => f.id === fileId);
    if (!fileToRetry) return;
    
    // Reset the file status
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, status: 'ready', error: null, progress: 0 } : f
    ));
    
    // Re-upload
    setTimeout(() => {
      uploadFile({ ...fileToRetry, status: 'ready', error: null, progress: 0 });
    }, 100);
  };

  const uploadFile = async (fileItem) => {
    if (!clientId) return;

    // STATIC DEMO PATH — no backend configured, so there is nothing to POST to. Record the
    // file locally under its REAL filename, advance the Docs tracker by one, and report
    // success. Without this the drop zone rejected every file with "Upload failed" the moment
    // apiFetch refused the call, which is the last thing you want on camera.
    if (IS_STATIC_DEMO) {
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading', progress: 40 } : f
      ));
      await new Promise(resolve => setTimeout(resolve, 350));
      // Read the file for measure evidence BEFORE recording it: a known strategy PDF
      // resolves to its binding, a D1/D2/D3 workbook is parsed and scored from its cells,
      // and anything else produces none. evidenceForFile never throws, so an unreadable
      // file still uploads — it simply carries no evidence.
      const coverage = await evidenceForFile(fileItem.file ?? { name: fileItem.name });
      // Say what the file did. A workbook that could not be read still uploads, and a
      // presenter who is not told will stand in front of a panel that did not move.
      const outcome = uploadOutcome(fileItem.name, coverage);
      const rec = addDocument({
        name: fileItem.name,
        size: fileItem.size,
        ...(coverage ? { coverage } : {}),
      });
      markPersonalizationChanged(); // re-arms the Evaluate interstitial
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id
          ? {
              ...f,
              status: 'complete',
              progress: 100,
              documentId: rec.id,
              coverageNote: outcome.note,
              coverageApplied: outcome.applied,
            }
          : f
      ));
      return;
    }

    const formData = new FormData();
    formData.append('file', fileItem.file);

    try {
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading', progress: 30 } : f
      ));

      const uploadResponse = await apiFetch(`${API_BASE_URL}/api/v2/documents/upload?clientId=${clientId}`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Upload failed (${uploadResponse.status})`);
      }

      const uploadResult = await uploadResponse.json();
      const documentId = uploadResult.data?.document_id;
      
      if (!documentId) {
        throw new Error('No document ID returned from server');
      }

      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'processing', progress: 60, documentId } : f
      ));

      // Wait for virus scan
      await new Promise(resolve => setTimeout(resolve, 2000));

      const processResponse = await apiFetch(`${API_BASE_URL}/api/v2/documents/${documentId}/process`, {
        method: 'POST'
      });

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Processing failed (${processResponse.status})`);
      }

      const processResult = await processResponse.json();

      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? {
          ...f,
          status: 'complete',
          progress: 100,
          stats: processResult.data?.stats
        } : f
      ));

    } catch (error) {
      console.error('Upload error:', error);
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? {
          ...f,
          status: 'error',
          error: error.message || 'Upload failed. Please try again.',
          progress: 0
        } : f
      ));
    }
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleUploadComplete = () => {
    setTimeout(() => {
      setActiveTab('library');
      fetchDocuments();
    }, 1500);
  };

  const handleRemoveDocument = async (documentId) => {
    // Demo-uploaded doc → remove locally. Delete is as real as upload: the document
    // leaves the list and, with it, whatever measure evidence it carried, so coverage
    // steps back down on its own. There is no separate tracker to decrement any more.
    if (IS_STATIC_DEMO || String(documentId).startsWith('demo-upload-')) {
      removeDocument(documentId);
      markPersonalizationChanged();
      setDocuments(prev => prev.filter(d => d.document_id !== documentId));
      return;
    }
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v2/documents/${documentId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setDocuments(prev => prev.filter(d => d.document_id !== documentId));
      }
    } catch (error) {
      console.error('Error removing document:', error);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen" style={{ backgroundColor: DVA_COLORS.slateLight }}>
      {/* EA Focus Banner */}
      {isEA && (
        <EAFocusBanner
          returnTo={returnTo}
          restoreScrollY={restoreScrollY}
        />
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: DVA_COLORS.navy }}>
            Document Management
          </h1>
          <p className="mt-2 text-sm" style={{ color: DVA_COLORS.slate }}>
            Add institutional context that strengthens rankings, ROI assumptions, and executive defensibility.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b mb-6" style={{ borderColor: DVA_COLORS.border }}>
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              style={{
                borderColor: activeTab === 'upload' ? DVA_COLORS.orange : 'transparent',
                color: activeTab === 'upload' ? DVA_COLORS.navy : DVA_COLORS.slate,
              }}
            >
              Upload Documents
            </button>
            <button
              onClick={() => setActiveTab('library')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              style={{
                borderColor: activeTab === 'library' ? DVA_COLORS.orange : 'transparent',
                color: activeTab === 'library' ? DVA_COLORS.navy : DVA_COLORS.slate,
              }}
            >
              Document Library ({allLibraryDocs.length})
            </button>
          </nav>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6 p-4 bg-white rounded-xl border" style={{ borderColor: DVA_COLORS.border }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: DVA_COLORS.slate }}>
              Documents Progress
            </span>
            <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
              {allLibraryDocs.length} of 3+ recommended
            </span>
          </div>
          <DocumentsProgressIndicator 
            documentCount={allLibraryDocs.length} 
            pendingCount={files.filter(f => f.status === 'complete').length}
          />
        </div>

        {/* Content */}
        {activeTab === 'upload' ? (
          <div className="space-y-6">
            {/* The two paths, stated once, above the drop zone. Path A below is unchanged. */}
            <EAUploadPathsPanel />

            {/* Upload Dropzone */}
            <div
              className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer`}
              style={{
                borderColor: dragActive ? DVA_COLORS.orange : DVA_COLORS.border,
                backgroundColor: dragActive ? DVA_COLORS.orangeLight : 
                                isDropzoneProcessing ? DVA_COLORS.slateLight : 'white',
                transform: dragActive ? 'scale(1.01)' : 'scale(1)',
                boxShadow: dragActive ? '0 8px 25px rgba(255, 110, 76, 0.15)' : 'none',
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                accept=".txt,.csv,.pdf,.md"
                onChange={handleChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isDropzoneProcessing}
              />

              {isDropzoneProcessing ? (
                /* Processing State */
                <>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: DVA_COLORS.orangeLight }}
                  >
                    <Loader className="w-8 h-8 animate-spin" style={{ color: DVA_COLORS.orange }} />
                  </div>
                  <p className="text-base font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
                    Processing your documents...
                  </p>
                  <p className="text-sm" style={{ color: DVA_COLORS.slate }}>
                    Uploading and extracting strategic signals
                  </p>
                </>
              ) : dragActive ? (
                /* Drag Active State */
                <>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: DVA_COLORS.orange }}
                  >
                    <Upload className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-base font-semibold mb-2" style={{ color: DVA_COLORS.orange }}>
                    Drop files to upload
                  </p>
                  <p className="text-sm" style={{ color: DVA_COLORS.slate }}>
                    Release to begin processing
                  </p>
                </>
              ) : (
                /* Idle State */
                <>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: DVA_COLORS.slateLight }}
                  >
                    <Upload className="w-8 h-8" style={{ color: DVA_COLORS.navy }} />
                  </div>

                  <p className="text-base font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
                    Drop strategic documents here to strengthen prioritization and ROI confidence.
                  </p>
                  <p className="text-sm mb-4" style={{ color: DVA_COLORS.slate }}>
                    Examples: Strategic plan, annual report, board priorities brief, governance policy.
                  </p>
                  <p className="text-xs" style={{ color: DVA_COLORS.slate }}>
                    Supported formats: TXT, CSV, PDF, MD (Max 25MB)
                  </p>
                </>
              )}
            </div>

            {/* How It Works - Collapsible */}
            <div className="bg-white rounded-xl border" style={{ borderColor: DVA_COLORS.border }}>
              <button
                onClick={() => setShowHowItWorks(!showHowItWorks)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
                  <span className="text-sm font-medium" style={{ color: DVA_COLORS.navy }}>
                    How DVA uses documents
                  </span>
                </div>
                {showHowItWorks ? (
                  <ChevronUp className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
                ) : (
                  <ChevronDown className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
                )}
              </button>

              {showHowItWorks && (
                <div className="px-4 pb-4">
                  <ol className="text-sm space-y-2" style={{ color: DVA_COLORS.slate }}>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>1.</span>
                      Upload strategic documents, reports, or data files
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>2.</span>
                      Documents are scanned for viruses and processed
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>3.</span>
                      Key facts, metrics, and strategic signals are extracted
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>4.</span>
                      Information enhances rankings, ROI, and defensibility
                    </li>
                  </ol>
                </div>
              )}
            </div>

            {/* File Queue / Recent Uploads */}
            {files.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold" style={{ color: DVA_COLORS.navy }}>
                    Recent Uploads
                  </h3>
                  {files.some(f => f.status === 'complete') && (
                    <button
                      onClick={() => {
                        setActiveTab('library');
                        fetchDocuments();
                      }}
                      className="text-xs font-medium transition-colors"
                      style={{ color: DVA_COLORS.orange }}
                    >
                      View in library →
                    </button>
                  )}
                </div>
                {files.map(file => (
                  <UploadFileCard
                    key={file.id}
                    file={file}
                    onRemove={removeFile}
                    onRetry={retryFile}
                  />
                ))}

                {/* Retry All Failed Button */}
                {files.some(f => f.status === 'error') && !uploading && (
                  <button
                    onClick={() => {
                      const failedFiles = files.filter(f => f.status === 'error' && !f.error?.includes('not supported') && !f.error?.includes('exceeds'));
                      failedFiles.forEach(f => retryFile(f.id));
                    }}
                    className="w-full py-3 rounded-xl font-semibold transition-colors"
                    style={{ 
                      backgroundColor: DVA_COLORS.orangeLight,
                      color: DVA_COLORS.orange,
                    }}
                  >
                    Retry Failed Uploads ({files.filter(f => f.status === 'error').length})
                  </button>
                )}

                {/* Clear Completed Button */}
                {files.some(f => f.status === 'complete') && !uploading && (
                  <button
                    onClick={() => {
                      setFiles(prev => prev.filter(f => f.status !== 'complete'));
                      fetchDocuments(); // Refresh library
                    }}
                    className="w-full py-2 rounded-xl text-sm font-medium transition-colors"
                    style={{ 
                      color: DVA_COLORS.slate,
                      backgroundColor: DVA_COLORS.slateLight,
                    }}
                  >
                    Clear completed uploads
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Document Library */
          <div>
            {loading ? (
              <div className="py-12 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: DVA_COLORS.orange }} />
                <p style={{ color: DVA_COLORS.slate }}>Loading documents...</p>
              </div>
            ) : allLibraryDocs.length === 0 ? (
              /* Empty State */
              <div className="py-12 text-center bg-white rounded-2xl border" style={{ borderColor: DVA_COLORS.border }}>
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: DVA_COLORS.slateLight }}
                >
                  <FileText className="w-8 h-8" style={{ color: DVA_COLORS.slate }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
                  No documents uploaded yet.
                </h3>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: DVA_COLORS.slate }}>
                  Uploading 2–3 strategic documents materially improves defensibility and ranking accuracy.
                </p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="px-6 py-2.5 rounded-xl text-white font-medium transition-colors"
                  style={{ backgroundColor: DVA_COLORS.navy }}
                >
                  Upload a document
                </button>
              </div>
            ) : (
              /* Document Cards */
              <div className="space-y-4">
                {allLibraryDocs.map((doc) => (
                  <DocumentCard
                    key={doc.document_id}
                    doc={doc}
                    onViewDetails={(doc) => setDetailsDoc(doc)}
                    onRemove={(doc) => setRemoveDoc(doc)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Details Modal */}
      <DocumentDetailsModal
        doc={detailsDoc}
        isOpen={!!detailsDoc}
        onClose={() => setDetailsDoc(null)}
      />

      {/* Remove Confirmation Modal */}
      <RemoveConfirmModal
        doc={removeDoc}
        isOpen={!!removeDoc}
        onClose={() => setRemoveDoc(null)}
        onConfirm={handleRemoveDocument}
      />
    </div>
  );
};

export default DocumentManagementPage;